/**
 * Guided tour of Billing & Analytics.
 *
 * This page answers a question in a particular order — what will this cost,
 * what drove it, does anything need doing — and that order is not obvious from
 * looking at it. An admin who arrives once a month should not have to rediscover
 * where the answer lives, so the tour walks the path once and then gets out of
 * the way.
 *
 * It runs itself the first time an admin opens the page and never again unless
 * they ask for it; the flag is per-browser, so it is a convenience and never
 * something the page depends on.
 *
 * Steps are anchored to `data-tour` attributes rather than to positions, so the
 * tour follows the layout instead of having to be re-measured whenever a card
 * moves. A step whose anchor is missing — an empty Templates tab, a panel a
 * failing query never rendered — is skipped rather than pointing at nothing.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react'
import { useBodyScrollLock, usePrefersReducedMotion } from './BillingPrimitives'

export const TOUR_SEEN_KEY = 'xtractai-billing-tour-v1'

/** Has this browser been shown the tour before? Storage may be blocked. */
export function hasSeenTour() {
  try {
    return localStorage.getItem(TOUR_SEEN_KEY) === 'done'
  } catch {
    return true   // Can't remember it was shown, so don't risk showing it twice.
  }
}

function markTourSeen() {
  try {
    localStorage.setItem(TOUR_SEEN_KEY, 'done')
  } catch {
    // A tour that cannot record itself is still worth running once.
  }
}

const STEPS = [
  {
    id: 'plan',
    anchor: 'plan',
    title: 'Start with the plan',
    body: 'Your tier, the monthly fee and what it includes. This panel stays put whichever tab you are on, because it is the figure everything else explains.',
  },
  {
    id: 'meter',
    anchor: 'meter',
    title: 'How much allowance is left',
    body: 'Solid is what you have already used. The hatched part is where this billing month lands at the current rate, and the upright marker is your included allowance. A bar past the marker means overage.',
  },
  {
    id: 'forecast',
    anchor: 'forecast',
    title: 'What it will cost by month end',
    body: 'A straight-line run rate, not a forecast model: if the rest of the month looks like the days so far, this is the bill. Hover any "?" on this page to see how a figure was worked out.',
  },
  {
    id: 'period',
    anchor: 'period',
    title: 'One period, every tab',
    body: 'This governs the whole page. Change it once and the chart, the templates and the workload panels all follow. The plan panel above is always the current billing month, whatever you pick here.',
  },
  {
    id: 'tabs',
    anchor: 'tabs',
    title: 'Six ways to look at it',
    body: 'Overview answers "should I do anything". Usage, Templates and People show where the volume came from. Costs breaks the Azure spend down by driver, template, person and how work arrived, and Reports takes it all away as CSV.',
  },
  {
    id: 'unit',
    tab: 'overview',
    anchor: 'unit-economics',
    title: 'Unit costs, not just totals',
    body: 'Totals grow with volume whatever you do. Cost per request, per document and per page tell you whether the service is getting cheaper or more expensive to run, and the last tile is what a page really costs you once the plan fee is spread across it.',
  },
  {
    id: 'insights',
    tab: 'overview',
    anchor: 'insights',
    title: 'Findings, with somewhere to go',
    body: 'Anything worth acting on is stated here in plain English, with the number behind it and a link to wherever you would act on it.',
  },
  {
    id: 'tiles',
    tab: 'usage',
    anchor: 'metric-tiles',
    title: 'The tiles drive the chart',
    body: 'Select a tile to plot it. Each one shows this billing month and compares it against the same point in the previous billing month, so a half-finished billing month is never measured against a whole one.',
  },
  {
    id: 'templates',
    tab: 'templates',
    anchor: 'templates-table',
    title: 'Where the volume concentrates',
    body: 'Request counts per template are measured; the cost beside them is an allocation, because usage is recorded per job rather than per template. Select any row for its daily pattern and a link to the template itself.',
  },
  {
    id: 'reports',
    tab: 'reports',
    anchor: 'reports-list',
    title: 'Take it with you',
    body: 'Five CSVs — usage by template, usage and plan position, the Azure breakdown, usage per person and the platform’s monthly report — each with a preview, or all of them in one file. You can restart this tour any time from the button in the header.',
  },
]

const GAP = 10
const POPOVER_W = 340

export default function BillingTour({ open, onClose, onRequestTab }) {
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState(null)
  const reduced = usePrefersReducedMotion()
  const popoverRef = useRef(null)
  const [placement, setPlacement] = useState({ top: 0, left: 0, arrow: 'top' })

  useBodyScrollLock(open)

  const step = STEPS[index]
  // Which way the reader is travelling, so a step that has to be skipped is
  // skipped in the direction they were already going.
  const dirRef = useRef(1)

  const anchorFor = i => document.querySelector(`[data-tour="${STEPS[i].anchor}"]`)

  // Switching tab first: the anchor for a step on another tab does not exist
  // until that tab is rendered.
  useEffect(() => {
    if (!open || !step?.tab) return
    onRequestTab(step.tab)
  }, [open, step, onRequestTab])

  useLayoutEffect(() => {
    if (!open) return
    let frame = 0
    let attempts = 0

    const measure = () => {
      const el = anchorFor(index)

      if (!el) {
        // A step that changes tab has no anchor until that tab has rendered,
        // so wait for it. Searching the neighbouring steps instead — which an
        // earlier version did — raced the tab switch and walked the tour
        // backwards into the step it had just left.
        if (attempts++ < 30) { frame = requestAnimationFrame(measure); return }

        // Genuinely absent: an empty tab, or a panel a failed query never
        // rendered. Carry on the way the reader was going.
        const dir = dirRef.current
        let i = index + dir
        while (i >= 0 && i < STEPS.length && !anchorFor(i)) i += dir
        if (i >= 0 && i < STEPS.length) setIndex(i)
        else onClose()
        return
      }

      el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' })
      // Let the smooth scroll settle before pinning the ring to the element.
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => setRect(el.getBoundingClientRect()))
      })
    }

    measure()
    return () => cancelAnimationFrame(frame)
  }, [open, index, reduced, onClose])

  // The ring is drawn in viewport coordinates, so anything that moves the page
  // under it has to move it too.
  useEffect(() => {
    if (!open) return
    let frame = 0
    const follow = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const el = document.querySelector(`[data-tour="${STEPS[index].anchor}"]`)
        if (el) setRect(el.getBoundingClientRect())
      })
    }
    window.addEventListener('resize', follow)
    window.addEventListener('scroll', follow, true)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('resize', follow)
      window.removeEventListener('scroll', follow, true)
    }
  }, [open, index])

  // Popover beside the ring, kept inside the viewport on every edge.
  useLayoutEffect(() => {
    if (!rect || !popoverRef.current) return
    const { height } = popoverRef.current.getBoundingClientRect()
    const below = rect.bottom + GAP
    const above = rect.top - height - GAP
    const fitsBelow = below + height < window.innerHeight - GAP

    const top = fitsBelow ? below : Math.max(GAP, above)
    const left = Math.max(
      GAP,
      Math.min(
        rect.left + rect.width / 2 - POPOVER_W / 2,
        window.innerWidth - POPOVER_W - GAP,
      ),
    )
    setPlacement({ top, left, arrow: fitsBelow ? 'top' : 'bottom' })
  }, [rect, index])

  const finish = useCallback(() => {
    markTourSeen()
    setIndex(0)
    setRect(null)
    onClose()
  }, [onClose])

  const next = useCallback(() => {
    dirRef.current = 1
    if (index >= STEPS.length - 1) finish()
    // `rect` is deliberately left as-is: the ring keeps showing the current
    // step's position — already dimmed, already in place — and glides to the
    // next one once it's measured, via the transition already on its
    // top/left/width/height. Nulling it here made the ring (and the dim
    // behind it) disappear for a beat between steps and pop back in, which
    // read as a flash rather than a move.
    else setIndex(i => i + 1)
  }, [index, finish])

  const back = useCallback(() => {
    dirRef.current = -1
    if (index > 0) setIndex(i => i - 1)
  }, [index])

  useEffect(() => {
    if (!open) return
    const onKey = e => {
      if (e.key === 'Escape') { e.preventDefault(); finish() }
      if (e.key === 'ArrowRight') { e.preventDefault(); next() }
      if (e.key === 'ArrowLeft') { e.preventDefault(); back() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, next, back, finish])

  useEffect(() => {
    if (open) popoverRef.current?.focus()
  }, [open, index])

  if (!open) return null

  const last = index === STEPS.length - 1
  const ease = reduced ? 'none' : 'all .32s cubic-bezier(.16,1,.3,1)'

  return createPortal(
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-label="Billing page tour">
      {/* The dim is the ring's own shadow, so the highlighted element stays lit
          without a second stacking context cut out of an overlay. */}
      {rect && (
        <div
          aria-hidden="true"
          className="absolute rounded-xl pointer-events-none"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.62), 0 0 0 2px rgba(255, 255, 255, 0.4)',
            transition: ease,
          }}
        />
      )}

      {/* Clicking the dim ends the tour, the way every overlay on this page does. */}
      <button
        type="button"
        aria-label="End tour"
        onClick={finish}
        className="absolute inset-0 w-full h-full cursor-default"
      />

      <div
        ref={popoverRef}
        tabIndex={-1}
        className="absolute rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900
                    shadow-2xl p-4 focus:outline-none"
        style={{ top: placement.top, left: placement.left, width: POPOVER_W, transition: ease }}
      >
        {/* Keyed on the step so the text itself gets a quick fade rather than
            hard-snapping mid-glide while the box above is still sliding into place. */}
        <div key={step.id} className={reduced ? '' : 'animate-panel-in'}>
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.09em] text-navy-700 dark:text-navy-300">
              Step {index + 1} of {STEPS.length}
            </span>
            <button
              type="button"
              onClick={finish}
              aria-label="End tour"
              className="ml-auto p-1 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
                         hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-gray-100 mt-1.5">{step.title}</h3>
          <p className="text-[12.5px] leading-relaxed text-gray-600 dark:text-gray-300 mt-1.5">{step.body}</p>
        </div>

        <div className="flex items-center gap-2 mt-4">
          <div className="flex items-center gap-1 mr-auto" aria-hidden="true">
            {STEPS.map((s, i) => (
              <i
                key={s.id}
                className={`h-1.5 rounded-full transition-all duration-300
                  ${i === index
                    ? 'w-4 bg-navy-600 dark:bg-navy-400'
                    : 'w-1.5 bg-gray-200 dark:bg-gray-700'}`}
              />
            ))}
          </div>

          {index > 0 && (
            <button
              type="button"
              onClick={back}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[12px] font-semibold rounded-lg
                         text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800
                         transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back
            </button>
          )}
          <button
            type="button"
            onClick={next}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold rounded-lg
                       bg-navy-600 hover:bg-navy-700 text-white transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
          >
            {last ? <><Check className="w-3.5 h-3.5" /> Done</> : <>Next <ArrowRight className="w-3.5 h-3.5" /></>}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
